import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { 
  Gamepad2, Plane, Dices, Cherry, Zap, Trophy, 
  Users, ArrowRight, Play, Volume2
} from "lucide-react";
import axios from "axios";

// Live wins simulator
const generateWin = () => {
  const names = ["Player***", "Lucky7***", "Win***", "Bet***", "Gold***"];
  const gameTypes = ["Aviator", "Slots", "Dice"];
  const amounts = [500, 1000, 2500, 5000, 10000, 25000, 50000];
  return {
    id: Date.now() + Math.random(),
    name: names[Math.floor(Math.random() * names.length)] + Math.floor(Math.random() * 100),
    game: gameTypes[Math.floor(Math.random() * gameTypes.length)],
    amount: amounts[Math.floor(Math.random() * amounts.length)],
    multiplier: (1 + Math.random() * 50).toFixed(2)
  };
};

export const LandingPage = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [liveWins, setLiveWins] = useState([]);
  const [jackpot, setJackpot] = useState(2547893);
  
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const initial = Array.from({ length: 5 }, generateWin);
    setLiveWins(initial);

    const interval = setInterval(() => {
      setLiveWins(prev => [generateWin(), ...prev.slice(0, 4)]);
      setJackpot(prev => prev + Math.floor(Math.random() * 500));
    }, 2500);

    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="min-h-screen bg-[#0F0F10] overflow-hidden">
      {/* Floating Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4">
        <div className="max-w-7xl mx-auto mt-4">
          <div className="flex items-center justify-between px-6 py-3 rounded-full bg-black/60 backdrop-blur-xl border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#00E701] flex items-center justify-center shadow-[0_0_20px_rgba(0,231,1,0.5)]">
                <Gamepad2 className="w-5 h-5 text-black" />
              </div>
              <span className="text-xl font-bold text-white font-['Rajdhani']">BetIndia</span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 rounded-full">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#00E701] hover:bg-[#00C201] text-black font-bold rounded-full px-6 shadow-[0_0_20px_rgba(0,231,1,0.4)]">
                  Play Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>
      
      {/* Hero Section - Full Screen */}
      <section className="relative min-h-screen flex items-center">
        {/* Video/Image Background */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1511512578047-dfb367046420?w=1920&h=1080&fit=crop"
            alt="Casino"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F10] via-transparent to-transparent" />
        </div>

        {/* Live Jackpot Ticker - Top */}
        <div className="absolute top-24 left-0 right-0 overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="flex items-center gap-8 mx-8">
                <span className="text-[#FFD600] font-bold">JACKPOT: ₹{jackpot.toLocaleString()}</span>
                <span className="text-white/40">•</span>
                <span className="text-[#00E701]">Player*** won ₹25,000 on Aviator</span>
                <span className="text-white/40">•</span>
                <span className="text-[#FF007A]">Lucky7*** hit 50x on Slots</span>
                <span className="text-white/40">•</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Main CTA */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF3B30]/20 border border-[#FF3B30]/30 mb-6">
                <div className="w-2 h-2 rounded-full bg-[#FF3B30] animate-pulse" />
                <span className="text-[#FF3B30] text-sm font-medium">5,234 players online</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white font-['Rajdhani'] mb-4 leading-tight">
                WIN BIG<br />
                <span className="text-[#00E701] drop-shadow-[0_0_30px_rgba(0,231,1,0.5)]">PLAY NOW</span>
              </h1>
              
              <p className="text-xl text-white/60 mb-8 max-w-lg">
                Aviator, Slots, Dice & Prediction Markets. Get ₹1,000 FREE on signup!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register" data-testid="hero-signup-btn">
                  <Button className="bg-[#00E701] hover:bg-[#00C201] text-black font-bold text-lg px-10 py-7 rounded-xl shadow-[0_0_40px_rgba(0,231,1,0.5)] hover:shadow-[0_0_60px_rgba(0,231,1,0.7)] transition-shadow">
                    <Zap className="w-6 h-6 mr-2" /> START PLAYING
                  </Button>
                </Link>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 text-lg px-8 py-7 rounded-xl">
                  <Play className="w-5 h-5 mr-2" /> Watch Demo
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="flex items-center gap-6 mt-10">
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#FFD600]" />
                  <span className="text-white/60 text-sm">₹10Cr+ Paid</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#007AFF]" />
                  <span className="text-white/60 text-sm">100K+ Players</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#00E701]" />
                  <span className="text-white/60 text-sm">Instant Payouts</span>
                </div>
              </div>
            </div>
            
            {/* Right - Live Wins */}
            <div className="hidden lg:block">
              <div className="rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 overflow-hidden">
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E701] animate-pulse" />
                    <span className="text-white font-bold">LIVE WINS</span>
                  </div>
                  <Volume2 className="w-4 h-4 text-white/40" />
                </div>
                
                <div className="divide-y divide-white/5">
                  {liveWins.map((win, idx) => (
                    <div 
                      key={win.id}
                      className={`p-4 flex items-center gap-4 ${idx === 0 ? 'bg-[#00E701]/10' : ''}`}
                      style={{ animation: idx === 0 ? 'slideIn 0.3s ease-out' : 'none' }}
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFD600] to-[#FF6B00] flex items-center justify-center text-black font-bold">
                        {win.name.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-medium">{win.name}</div>
                        <div className="text-white/50 text-sm">{win.game}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[#00E701] font-mono font-bold text-lg">
                          ₹{win.amount.toLocaleString()}
                        </div>
                        <div className="text-white/40 text-sm">{win.multiplier}x</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Games */}
      <section className="py-20 px-4 relative">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-white font-['Rajdhani'] flex items-center gap-3">
              <Zap className="w-8 h-8 text-[#FFD600]" />
              FEATURED GAMES
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Aviator */}
            <Link to="/register" className="group relative aspect-[3/4] rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1436891620584-47fd0e565afb?w=400&h=500&fit=crop"
                alt="Aviator"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute top-3 left-3 px-2 py-1 rounded bg-[#FF3B30] text-white text-xs font-bold flex items-center gap-1">
                <Zap className="w-3 h-3" /> HOT
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <Plane className="w-8 h-8 text-[#FF3B30] mb-2" />
                <h3 className="text-xl font-bold text-white font-['Rajdhani']">Aviator</h3>
                <p className="text-white/60 text-sm mb-2">Up to 100x</p>
                <div className="h-1 bg-white/10 rounded overflow-hidden">
                  <div className="h-full w-3/4 bg-[#FF3B30] rounded" />
                </div>
              </div>
            </Link>
            
            {/* Slots */}
            <Link to="/register" className="group relative aspect-[3/4] rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1596838132731-3301c3fd4317?w=400&h=500&fit=crop"
                alt="Slots"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <Cherry className="w-8 h-8 text-[#FF007A] mb-2" />
                <h3 className="text-xl font-bold text-white font-['Rajdhani']">Slots</h3>
                <p className="text-white/60 text-sm mb-2">Up to 50x</p>
                <div className="h-1 bg-white/10 rounded overflow-hidden">
                  <div className="h-full w-2/3 bg-[#FF007A] rounded" />
                </div>
              </div>
            </Link>
            
            {/* Dice */}
            <Link to="/register" className="group relative aspect-[3/4] rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1522069213448-443a614da9b6?w=400&h=500&fit=crop"
                alt="Dice"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <Dices className="w-8 h-8 text-[#007AFF] mb-2" />
                <h3 className="text-xl font-bold text-white font-['Rajdhani']">Dice</h3>
                <p className="text-white/60 text-sm mb-2">Up to 98x</p>
                <div className="h-1 bg-white/10 rounded overflow-hidden">
                  <div className="h-full w-1/2 bg-[#007AFF] rounded" />
                </div>
              </div>
            </Link>
            
            {/* News Betting */}
            <Link to="/register" className="group relative aspect-[3/4] rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=500&fit=crop"
                alt="News Betting"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute top-3 left-3 px-2 py-1 rounded bg-[#00E701] text-black text-xs font-bold">NEW</div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <Trophy className="w-8 h-8 text-[#00E701] mb-2" />
                <h3 className="text-xl font-bold text-white font-['Rajdhani']">Predictions</h3>
                <p className="text-white/60 text-sm mb-2">Prediction Markets</p>
                <div className="h-1 bg-white/10 rounded overflow-hidden">
                  <div className="h-full w-4/5 bg-[#00E701] rounded" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="p-12 rounded-3xl bg-gradient-to-r from-[#00E701]/20 via-[#007AFF]/10 to-[#FF007A]/20 border border-white/10">
            <h2 className="text-4xl font-bold text-white font-['Rajdhani'] mb-4">
              GET ₹1,000 FREE
            </h2>
            <p className="text-white/60 text-lg mb-8">
              Sign up now and start winning instantly
            </p>
            <Link to="/register">
              <Button className="bg-[#00E701] hover:bg-[#00C201] text-black font-bold text-xl px-12 py-8 rounded-xl shadow-[0_0_50px_rgba(0,231,1,0.5)]">
                CLAIM BONUS <ArrowRight className="w-6 h-6 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#00E701] flex items-center justify-center">
              <Gamepad2 className="w-4 h-4 text-black" />
            </div>
            <span className="text-white font-['Rajdhani'] font-bold">BetIndia</span>
          </div>
          <p className="text-white/40 text-sm text-center">
            18+ | Play Responsibly | © 2025 BetIndia.com
          </p>
        </div>
      </footer>

      {/* CSS for marquee animation */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
